import { validateOptionalEditorText } from "./editorValidation.js";

export type IftarSlotEditorInput = {
  slotId: string;
  modeId: string;
  dayNumber: string;
  label: string;
  labelAr: string;
  amountUsd: string;
};

export type IftarSlotEditorDraft = {
  slotId: string;
  modeId: string;
  dayNumber: number;
  label: string;
  labelAr: string;
  amountCents: number;
  currency: "usd";
  status: "available";
  source: "experience_four";
};

export type IftarSlotEditorResult =
  | {
      ok: true;
      draft: IftarSlotEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type IftarSlotEditorFixture = {
  amountCents: number;
  dayNumber: number;
  id: string;
  label: string;
  labelAr: string;
  modeId: string;
};

const idLimit = 80;
const labelLimit = 120;
const maxAmountCents = 100000;

export function buildIftarSlotEditorInputFromFixture(
  fixture: IftarSlotEditorFixture,
): IftarSlotEditorInput {
  return {
    amountUsd: formatUsdInputValue(fixture.amountCents),
    dayNumber: String(fixture.dayNumber),
    label: fixture.label,
    labelAr: fixture.labelAr,
    modeId: fixture.modeId,
    slotId: fixture.id,
  };
}

export function buildDefaultIftarSlotEditorInput(): IftarSlotEditorInput {
  return {
    amountUsd: "500",
    dayNumber: "1",
    label: "Ramadan day 1 iftar",
    labelAr: "إفطار اليوم الأول من رمضان",
    modeId: "ramadan-2027",
    slotId: "ramadan-2027-day-01",
  };
}

export function buildEmptyIftarSlotEditorInput(): IftarSlotEditorInput {
  return {
    amountUsd: "",
    dayNumber: "",
    label: "",
    labelAr: "",
    modeId: "",
    slotId: "",
  };
}

export function evaluateIftarSlotEditorInput(input: IftarSlotEditorInput): IftarSlotEditorResult {
  const slotId = normalizeIftarSlotDocumentId(input.slotId);
  const modeId = normalizeIftarSlotDocumentId(input.modeId);
  const dayNumber = parseDayNumber(input.dayNumber);
  const label = input.label.trim();
  const labelAr = input.labelAr.trim();
  const amountCents = parseUsdInputToCents(input.amountUsd);
  const errors: string[] = [];

  validateRequiredText("Iftar slot id", slotId, idLimit, errors);
  validateRequiredText("Ramadan mode id", modeId, idLimit, errors);
  validateRequiredText("English label", label, labelLimit, errors);
  validateOptionalEditorText("Arabic label", labelAr, labelLimit, errors);

  if (dayNumber === undefined) {
    errors.push("Ramadan day must be a whole number from 1 to 30.");
  }

  if (amountCents === undefined) {
    errors.push("Sponsorship amount must be a valid USD amount.");
  } else if (amountCents <= 0 || amountCents > maxAmountCents) {
    errors.push("Sponsorship amount must be greater than $0 and at most $1,000.");
  }

  if (errors.length > 0 || dayNumber === undefined || amountCents === undefined) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      amountCents,
      currency: "usd",
      dayNumber,
      label,
      labelAr,
      modeId,
      slotId,
      source: "experience_four",
      status: "available",
    },
  };
}

export function normalizeIftarSlotDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, idLimit);
}

function parseDayNumber(value: string): number | undefined {
  const trimmed = value.trim();

  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }

  const dayNumber = Number(trimmed);
  return Number.isInteger(dayNumber) && dayNumber >= 1 && dayNumber <= 30 ? dayNumber : undefined;
}

function formatUsdInputValue(amountCents: number): string {
  if (amountCents % 100 === 0) {
    return String(amountCents / 100);
  }

  return (amountCents / 100).toFixed(2);
}

function parseUsdInputToCents(value: string): number | undefined {
  const trimmed = value.trim();

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return undefined;
  }

  const parts = trimmed.split(".");
  const dollarsPart = parts[0];
  const centsPart = parts[1] ?? "";

  if (!dollarsPart) {
    return undefined;
  }

  const dollars = Number(dollarsPart);

  if (!Number.isSafeInteger(dollars)) {
    return undefined;
  }

  const cents = Number(centsPart.padEnd(2, "0"));
  const totalCents = dollars * 100 + cents;
  return Number.isSafeInteger(totalCents) ? totalCents : undefined;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
